import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { inr } from "@/theme";
import { useAdminTheme } from "@/admin/AdminTheme";
import { ACard, AButton, Badge, STATUS_TONE, AdminPageTitle } from "@/admin/ui";
import { Spinner } from "@/components/common";
import { toast } from "sonner";

const NEXT = { created: ["paid", "cancelled"], paid: ["shipped", "cancelled"], shipped: ["delivered", "cancelled"], delivered: [], cancelled: [] };

export default function AdminOrderDetail() {
  const { c } = useAdminTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);

  const load = useCallback(() => {
    api("/admin/orders").then((all) => setOrder(all.find((o) => o.id === id) || null)).catch(() => setOrder(null));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    try { await api(`/admin/orders/${id}/status`, { method: "PUT", body: { status } }); toast.success(`Order → ${status}`); load(); }
    catch (e) { toast.error(e.message); }
  };

  if (!order) return <Spinner full />;

  return (
    <div className="fade-up">
      <AdminPageTitle title={`Order #${order.id.slice(0, 8)}`} subtitle={new Date(order.created_at).toLocaleString()}
        action={<AButton variant="outline" onClick={() => navigate("/admin/orders")}>← Back</AButton>} />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px,1fr))", gap: 16 }}>
        <ACard>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <h3 className="font-display" style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Items</h3>
            <Badge tone={STATUS_TONE[order.status]}>{order.status}</Badge>
          </div>
          {order.items.map((it) => (
            <div key={it.product_id} style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: `1px solid ${c.divider}` }}>
              {it.image && <img src={it.image} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: "cover" }} />}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13.5 }}>{it.name}</div>
                <div style={{ fontSize: 12.5, color: c.mutedText }}>Qty {it.quantity} · {inr(it.price)}</div>
              </div>
              <div style={{ fontWeight: 700 }}>{inr(it.line_total)}</div>
            </div>
          ))}
          <div style={{ marginTop: 12 }}>
            <Row c={c} label="Subtotal" value={inr(order.subtotal)} />
            {order.discount > 0 && <Row c={c} label="Discount" value={`−${inr(order.discount)}`} />}
            {order.gift_wrap_fee > 0 && <Row c={c} label="Gift wrap" value={inr(order.gift_wrap_fee)} />}
            <Row c={c} label="Total" value={inr(order.amount)} bold />
          </div>
        </ACard>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <ACard>
            <h3 className="font-display" style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 700 }}>Customer</h3>
            <div style={{ fontSize: 14, lineHeight: 1.7 }}>{order.shipping_name}<br />{order.shipping_phone}<br /><span style={{ color: c.mutedText }}>{order.shipping_address}</span></div>
            {order.gift_note && <div style={{ marginTop: 10, padding: 10, background: c.surfaceAlt, borderRadius: 8, fontSize: 13 }}>🎁 {order.gift_note}</div>}
          </ACard>
          <ACard>
            <h3 className="font-display" style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 700 }}>Update Status</h3>
            {NEXT[order.status]?.length ? (
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {NEXT[order.status].map((s) => (
                  <AButton key={s} data-testid={`set-status-${s}`} variant={s === "cancelled" ? "danger" : "primary"} onClick={() => setStatus(s)} style={{ textTransform: "capitalize" }}>Mark {s}</AButton>
                ))}
              </div>
            ) : <p style={{ color: c.mutedText, margin: 0 }}>No further transitions available.</p>}
          </ACard>
        </div>
      </div>
    </div>
  );
}

function Row({ c, label, value, bold }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: bold ? 16 : 13.5, fontWeight: bold ? 700 : 400, color: c.onSurface, borderTop: bold ? `1px solid ${c.divider}` : "none", marginTop: bold ? 6 : 0, paddingTop: bold ? 10 : 4 }}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}
